import { useMemo, useState } from 'react';
import { BuscaProcesso } from '../components/BuscaProcesso';
import { ModelosTemplateTable } from '../components/ModelosTemplateTable';
import { PageShell } from '../components/PageShell';
import { useConferirPeticao, useDadosPeticao, useGerarPeticaoModelo, useModelosPeca } from '../hooks';
import { TIPOS_PECA } from '../tiposPeca';
import type { ConferirResponse, GerarPeticaoResponse, PrazoCard } from '../types';

const SRC_LABEL: Record<string, string> = {
  autos: 'autos', cliente: 'cadastro', datajud: 'DataJud',
};

/**
 * Aba "Peticionar com modelo".
 *  ESQUERDA  — tabela de modelos .docx por tipo de peça (subir/trocar/excluir).
 *  DIREITA   — escolher processo → ver dados → escolher a peça → conferir os
 *              campos Jinja2 do modelo contra os autos → preencher manualmente o
 *              que faltar. (Gerar + concluir p/ protocolo entram nas fases 3 e 4.)
 */
export function PeticionarComModelo() {
  const [card, setCard] = useState<PrazoCard | null>(null);
  const [pieceKey, setPieceKey] = useState<string>('');
  const [conf, setConf] = useState<ConferirResponse | null>(null);
  const [manuais, setManuais] = useState<Record<string, string>>({});
  const [gerada, setGerada] = useState<GerarPeticaoResponse | null>(null);

  const { data: dados, isLoading: loadingDados } = useDadosPeticao(card?.id_prazo ?? null);
  const { data: modelos } = useModelosPeca();
  const conferir = useConferirPeticao();
  const gerar = useGerarPeticaoModelo();

  // Só tipos que têm modelo cadastrado podem ser peticionados.
  const tiposComModelo = useMemo(
    () => TIPOS_PECA.filter((t) => modelos?.[t.key]?.tem_modelo),
    [modelos],
  );

  function escolherProcesso(c: PrazoCard) {
    setCard(c);
    setPieceKey('');
    setConf(null);
    setManuais({});
    setGerada(null);
  }

  function escolherPeca(key: string) {
    setPieceKey(key);
    setConf(null);
    setManuais({});
    setGerada(null);
    if (!key || !card) return;
    conferir.mutate(
      { idPrazo: card.id_prazo, pieceKey: key },
      { onSuccess: (r) => setConf(r) },
    );
  }

  function gerarPeticao() {
    if (!card || !pieceKey) return;
    gerar.mutate(
      { idPrazo: card.id_prazo, pieceKey, overrides: manuais },
      { onSuccess: (r) => setGerada(r) },
    );
  }

  const missing = conf?.conferencia.missing ?? [];
  const ifBalance = conf?.conferencia.if_balance ?? true;
  // bloqueia geração com Jinja desbalanceado ou placeholders não-reconhecidos
  const podeGerar = !!conf && ifBalance && (conf.conferencia.unknown.length === 0);

  return (
    <PageShell
      titulo={<h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">✍️ Peticionar com modelo</h1>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ─── ESQUERDA: modelos ─── */}
        <section className="lg:border-r lg:border-slate-200 lg:dark:border-slate-800 lg:pr-6">
          <ModelosTemplateTable />
        </section>

        {/* ─── DIREITA: peticionar num processo ─── */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            🖋️ Gerar petição num processo
          </h2>

          {/* 1) escolher processo */}
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">1. Processo</label>
            <BuscaProcesso
              onSelect={escolherProcesso}
              placeholder="Buscar processo nº…"
              dica="escolha o processo para peticionar"
              larguraInput="w-full"
            />
          </div>

          {card && (
            <>
              {/* 2) dados básicos */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/30 p-3">
                <div className="text-[11px] font-medium text-slate-500 mb-2">2. Dados do processo</div>
                {loadingDados ? (
                  <p className="text-xs text-slate-400">Carregando dados…</p>
                ) : dados ? (
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {dados.campos.filter((c) => c.valor).map((c) => (
                      <div key={c.key} className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-wide text-slate-400">
                          {c.label}
                          {c.source && <span className="ml-1 normal-case text-slate-300">· {SRC_LABEL[c.source] ?? c.source}</span>}
                        </dt>
                        <dd className="text-xs text-slate-700 dark:text-slate-200 truncate" title={c.valor}>{c.valor}</dd>
                      </div>
                    ))}
                    {dados.campos.every((c) => !c.valor) && (
                      <p className="text-xs text-slate-400 col-span-2">Nenhum dado encontrado automaticamente.</p>
                    )}
                  </dl>
                ) : null}
              </div>

              {/* 3) escolher a peça */}
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">3. Tipo de peça</label>
                {tiposComModelo.length === 0 ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Nenhum modelo cadastrado ainda — suba um modelo na tabela à esquerda.
                  </p>
                ) : (
                  <select
                    value={pieceKey}
                    onChange={(e) => escolherPeca(e.target.value)}
                    className="w-full text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">Selecione a peça…</option>
                    {tiposComModelo.map((t) => (
                      <option key={t.key} value={t.key}>{t.rotulo}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* 4) conferência + campos manuais */}
              {conferir.isPending && <p className="text-xs text-slate-400">Conferindo o modelo…</p>}
              {conferir.isError && (
                <p className="text-xs text-rose-500">
                  {conferir.error instanceof Error ? conferir.error.message : 'Falha na conferência'}
                </p>
              )}

              {conf && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/30 p-3 space-y-2">
                    <div className="text-[11px] font-medium text-slate-500">
                      4. Conferência do modelo <span className="text-slate-400">— {conf.rotulo}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                        ✓ {conf.conferencia.ok.length} preenchidos
                      </span>
                      {conf.conferencia.empty.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                          🟡 {conf.conferencia.empty.length} vazios
                        </span>
                      )}
                      {conf.conferencia.unknown.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">
                          🔴 {conf.conferencia.unknown.length} não reconhecidos
                        </span>
                      )}
                      {conf.conferencia.if_count > 0 && (
                        <span className={`px-2 py-0.5 rounded-full ${ifBalance ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300' : 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'}`}>
                          {ifBalance ? '✓' : '❌'} {conf.conferencia.if_count} {'{% if %}'} × {conf.conferencia.endif_count} {'{% endif %}'}
                        </span>
                      )}
                    </div>
                    {!ifBalance && (
                      <p className="text-[11px] text-rose-500">
                        Blocos Jinja desbalanceados — corrija o modelo antes de gerar.
                      </p>
                    )}
                    {conf.conferencia.unknown.length > 0 && (
                      <ul className="text-[11px] text-rose-600 dark:text-rose-400 space-y-0.5">
                        {conf.conferencia.unknown.map((u) => (
                          <li key={u.name}>
                            <code className="px-1 rounded bg-rose-50 dark:bg-rose-950/40">{`{{${u.name}}}`}</code>
                            {u.suggestion && <span className="text-slate-400"> — quis dizer <code>{`{{${u.suggestion}}}`}</code>?</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* campos manuais — um input por placeholder faltante */}
                  {missing.length > 0 && (
                    <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/20 p-3 space-y-2">
                      <div className="text-[11px] font-medium text-amber-700 dark:text-amber-300">
                        5. Preencher manualmente ({missing.length})
                      </div>
                      {missing.map((m) => (
                        <div key={m.name}>
                          <label className="block text-[10px] text-slate-500 mb-0.5">
                            {m.label ?? m.name} <code className="text-slate-400">{`{{${m.name}}}`}</code>
                          </label>
                          <input
                            type="text"
                            value={manuais[m.name] ?? ''}
                            onChange={(e) => setManuais((p) => ({ ...p, [m.name]: e.target.value }))}
                            placeholder={`valor para ${m.name}…`}
                            className="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Gerar petição (Fase 3) */}
                  <button
                    onClick={gerarPeticao}
                    disabled={!podeGerar || gerar.isPending}
                    title={podeGerar ? 'Renderiza o modelo e salva no Drive do processo' : 'Corrija o modelo (Jinja/placeholders) antes de gerar'}
                    className="w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {gerar.isPending ? 'Gerando…' : '📝 Gerar petição'}
                  </button>
                  {gerar.isError && (
                    <p className="text-xs text-rose-500">
                      {gerar.error instanceof Error ? gerar.error.message : 'Falha ao gerar'}
                    </p>
                  )}

                  {gerada && (
                    <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-950/20 p-3 space-y-2">
                      <div className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                        ✓ Petição gerada e salva no Drive
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-300 truncate" title={gerada.nome}>
                        📄 {gerada.nome}
                      </div>
                      {gerada.docx_url && (
                        <a
                          href={gerada.docx_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50"
                        >
                          ↗ Abrir no Drive
                        </a>
                      )}
                      <p className="text-[10px] text-slate-400">
                        A peça já é a do card (aparece em "Exibir petição"). Concluir p/ protocolo entra na próxima fase.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </PageShell>
  );
}
